import { useQuery, useMutation, useQueryClient } from "react-query";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import { useApp } from "../../AppProvider";

export const useWaterQualityForm = (wellNdx) => {
  const { getAccessTokenSilently } = useAuth0();
  const { doToast } = useApp();
  const queryClient = useQueryClient();

  // Fetch wells that have water quality data
  const listWqWellsQuery = useQuery(
    ["waterQuality", "wellsWithWq"],
    async () => {
      const token = await getAccessTokenSilently();
      const { data } = await axios.get(
        `${process.env.REACT_APP_ENDPOINT}/api/water-quality/list-wq-wells`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    {
      staleTime: 30 * 1000, // 30 seconds
      keepPreviousData: true,
    }
  );

  // Fetch water quality parameters for selection
  const wqParametersQuery = useQuery(
    ["waterQuality", "parameters"],
    async () => {
      const token = await getAccessTokenSilently();
      const { data } = await axios.get(
        `${process.env.REACT_APP_ENDPOINT}/api/water-quality/parameters`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      keepPreviousData: true,
    }
  );

  // Fetch water quality entries for selected well
  const waterQualityEntriesQuery = useQuery(
    ["waterQuality", "entries", wellNdx],
    async () => {
      const token = await getAccessTokenSilently();
      const { data } = await axios.get(
        `${process.env.REACT_APP_ENDPOINT}/api/water-quality/water-quality-list`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { well_ndx: wellNdx },
        }
      );
      return data;
    },
    {
      enabled: !!wellNdx,
      staleTime: 30 * 1000,
      keepPreviousData: true,
    }
  );

  // Save water quality entry mutation
  const saveWaterQualityMutation = useMutation(
    async (entry) => {
      const token = await getAccessTokenSilently();
      const { data } = await axios.put(
        `${process.env.REACT_APP_ENDPOINT}/api/water-quality/water-quality-entry`,
        { entry },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["waterQuality", "entries", wellNdx]);
        doToast("success", "Water quality entry saved successfully");
      },
      onError: (error) => {
        console.error("Error saving water quality entry:", error);
        doToast("error", "Failed to save water quality entry");
      },
    }
  );

  // Delete water quality entry mutation
  const deleteWaterQualityMutation = useMutation(
    async (ndx) => {
      const token = await getAccessTokenSilently();
      const { data } = await axios.delete(
        `${process.env.REACT_APP_ENDPOINT}/api/water-quality/water-quality-entry/${ndx}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["waterQuality", "entries", wellNdx]);
        doToast("success", "Water quality entry deleted successfully");
      },
      onError: (error) => {
        console.error("Error deleting water quality entry:", error);
        doToast("error", "Failed to delete water quality entry");
      },
    }
  );

  // Upload CSV mutation
  const uploadCsvMutation = useMutation(
    async ({ file }) => {
      const token = await getAccessTokenSilently();
      // For now, just mock the file upload
      // In production, this would send the actual file as FormData
      const { data } = await axios.post(
        `${process.env.REACT_APP_ENDPOINT}/api/water-quality/upload-csv`,
        { file_name: file?.name || "mock.csv" },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["waterQuality", "staging"]);
        doToast(
          "success",
          `CSV uploaded successfully. ${data.records_processed} records processed.`
        );
      },
      onError: (error) => {
        console.error("Error uploading CSV:", error);
        doToast("error", "Failed to upload CSV file");
      },
    }
  );

  // Fetch staging records - all pending records, not filtered by well
  const stagingRecordsQuery = useQuery(
    ["waterQuality", "staging"],
    async () => {
      const token = await getAccessTokenSilently();
      const { data } = await axios.get(
        `${process.env.REACT_APP_ENDPOINT}/api/water-quality/staging`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { status: "pending" }, // Only fetch pending records
        }
      );
      return data;
    },
    {
      staleTime: 30 * 1000,
      keepPreviousData: true,
    }
  );

  // Approve/reject staging record mutation
  const approveStagingMutation = useMutation(
    async ({ ndx, status, validation_errors }) => {
      const token = await getAccessTokenSilently();
      const { data } = await axios.put(
        `${process.env.REACT_APP_ENDPOINT}/api/water-quality/staging/${ndx}`,
        { status, validation_errors },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["waterQuality", "staging"]);
        queryClient.invalidateQueries(["waterQuality", "entries", wellNdx]);
        doToast("success", "Record processed successfully");
      },
      onError: (error) => {
        console.error("Error processing staging record:", error);
        doToast("error", "Failed to process staging record");
      },
    }
  );

  // Bulk approve staging records mutation
  const bulkApproveStagingMutation = useMutation(
    async ({ batch_id, record_ids }) => {
      const token = await getAccessTokenSilently();
      const { data } = await axios.post(
        `${process.env.REACT_APP_ENDPOINT}/api/water-quality/staging/bulk-approve`,
        { batch_id, record_ids },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["waterQuality", "staging"]);
        queryClient.invalidateQueries(["waterQuality", "entries", wellNdx]);
        doToast("success", data.message);
      },
      onError: (error) => {
        console.error("Error bulk approving staging records:", error);
        doToast("error", "Failed to bulk approve staging records");
      },
    }
  );

  return {
    listWqWellsQuery,
    wqParametersQuery,
    waterQualityEntriesQuery,
    saveWaterQualityMutation,
    deleteWaterQualityMutation,
    uploadCsvMutation,
    stagingRecordsQuery,
    approveStagingMutation,
    bulkApproveStagingMutation,
  };
};
